import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["grid", "frame", "refreshLabel", "total", "totalUsers", "totalViews", "queueStatus"]

  static values = {
    maxConcurrent: { type: Number, default: 2 },
    sortDelay: { type: Number, default: 250 }
  }

  connect() {
    this.inFlight = 0
    this.pendingQueue = []
    this.sortTimer = null

    // Record initial server sequence for stable tie-breaks
    this.frameTargets.forEach((frame, index) => {
      frame.dataset.initialIndex = index.toString()
      this.enqueueFrame(frame)
    })
    this.processQueue()
  }

  disconnect() {
    this.pendingQueue = []
    if (this.sortTimer) {
      clearTimeout(this.sortTimer)
      this.sortTimer = null
    }
  }

  // Teardown guard: in-flight fetch callbacks still run after disconnect().
  // Every sort entry point bails when the controller element is detached, so
  // no new timer is created and no sort executes on detached DOM.
  isTornDown() {
    return !this.element.isConnected
  }

  refreshAll() {
    if (this.frameTargets.length === 0) return;

    if (this.hasRefreshLabelTarget) {
      const button = this.refreshLabelTarget.closest("button")
      if (button) {
        button.disabled = true
        button.classList.add("opacity-50", "cursor-not-allowed")
      }
      this.refreshLabelTarget.textContent = "Refreshing all analytics..."
    }
    this.frameTargets.forEach(frame => {
      this.enqueueFrame(frame, true)
    })
    this.processQueue()
  }

  refreshFrame(event) {
    const key = event.currentTarget.dataset.analyticsKey
    const frame = this.frameTargets.find(f => f.dataset.analyticsKey === key)
    if (frame) {
      this.enqueueFrame(frame, true)
      this.processQueue()
    }
  }

  retryFrame(event) {
    const key = event.currentTarget.dataset.analyticsKey
    const frame = this.frameTargets.find(f => f.dataset.analyticsKey === key)
    if (frame) {
      this.enqueueFrame(frame, true)
      this.processQueue()
    }
  }

  enqueueFrame(frame, isRefresh = false) {
    const src = frame.dataset.src
    if (!src) return

    // Remove existing pending entry for this frame to avoid duplicates
    this.pendingQueue = this.pendingQueue.filter(item => item.frame !== frame)

    // Set busy state
    frame.setAttribute("aria-busy", "true")

    // Add to queue
    this.pendingQueue.push({ frame, src, isRefresh })
  }

  processQueue() {
    while (this.inFlight < this.maxConcurrentValue && this.pendingQueue.length > 0) {
      const { frame, src, isRefresh } = this.pendingQueue.shift()
      this.loadFrame(frame, src, isRefresh)
    }
  }

  loadFrame(frame, src, isRefresh = false) {
    this.inFlight++

    // Only add refresh=true when explicitly refreshing
    let url = src
    if (isRefresh) {
      const separator = src.includes("?") ? "&" : "?"
      url = src.includes("refresh=true") ? src : `${src}${separator}refresh=true`
    }

    fetch(url, {
      headers: {
        "Accept": "text/html",
        "Turbo-Frame": frame.id
      }
    })
      .then(response => {
        if (response.redirected && response.url.includes("/auth/login")) {
          window.location.reload()
          return
        }
        if (response.ok) {
          return response.text()
        }
        throw new Error(`HTTP ${response.status}`)
      })
      .then(html => {
        const parser = new DOMParser()
        const doc = parser.parseFromString(html, "text/html")
        const content = doc.body.firstElementChild
        if (content) {
          // content is itself a <turbo-frame>. Transfer its child nodes
          // into the existing frame to avoid nested <turbo-frame> elements.
          const frag = document.createDocumentFragment()
          Array.from(content.childNodes).forEach(child => frag.appendChild(child))
          frame.replaceChildren(frag)

          // Copy the incoming frame's dataset so updateSummary can read
          // analytics-loaded, analytics-users-count, and analytics-views-count.
          if (content.dataset.analyticsLoaded) {
            frame.dataset.analyticsLoaded = content.dataset.analyticsLoaded
          }
          if (content.dataset.analyticsUsersCount) {
            frame.dataset.analyticsUsersCount = content.dataset.analyticsUsersCount
          }
          if (content.dataset.analyticsViewsCount) {
            frame.dataset.analyticsViewsCount = content.dataset.analyticsViewsCount
          }
        }
        frame.setAttribute("aria-busy", "false")
        this.updateSummary()
        this.scheduleSort()
      })
      .catch(error => {
        console.error(`Failed to load analytics for ${frame.dataset.analyticsKey}:`, error)
        frame.setAttribute("aria-busy", "false")
        frame.dataset.analyticsLoaded = "error"
        frame.dataset.analyticsUsersCount = "0"
        frame.dataset.analyticsViewsCount = "0"
        frame.replaceChildren(this.buildErrorElement(frame.dataset.analyticsKey))
        this.updateSummary()
        this.scheduleSort()
      })
      .finally(() => {
        this.inFlight--
        if (this.pendingQueue.length === 0 && this.inFlight === 0) {
          this.flushSort()
          if (this.hasRefreshLabelTarget) {
            const button = this.refreshLabelTarget.closest("button")
            if (button) {
              button.disabled = false
              button.classList.remove("opacity-50", "cursor-not-allowed")
            }
            this.refreshLabelTarget.textContent = "Refresh All"
          }
        }
        this.processQueue()
      })
  }

  scheduleSort() {
    if (this.isTornDown()) return
    if (this.sortTimer) {
      clearTimeout(this.sortTimer)
    }
    this.sortTimer = setTimeout(() => {
      this.sortTimer = null
      this.sortGrid()
    }, this.sortDelayValue)
  }

  flushSort() {
    if (this.sortTimer) {
      clearTimeout(this.sortTimer)
      this.sortTimer = null
    }
    if (this.isTornDown()) return
    this.sortGrid()
  }

  sortGrid() {
    if (this.isTornDown()) return
    if (!this.hasGridTarget) return
    const grid = this.gridTarget

    const currentFrames = Array.from(grid.children)
    if (currentFrames.length <= 1) return

    const sortedFrames = [...currentFrames].sort((a, b) => {
      const isLoadedA = Boolean(a.dataset.analyticsLoaded)
      const isLoadedB = Boolean(b.dataset.analyticsLoaded)

      // Rule 1: Loaded frames appear before unloaded skeleton frames
      if (isLoadedA && !isLoadedB) return -1
      if (!isLoadedA && isLoadedB) return 1

      // Rule 2: Both unloaded skeletons preserve original initial sequence
      if (!isLoadedA && !isLoadedB) {
        return Number(a.dataset.initialIndex || 0) - Number(b.dataset.initialIndex || 0)
      }

      // Rule 3: Both loaded cards sort descending by active users count
      const usersA = Number(a.dataset.analyticsUsersCount) || 0
      const usersB = Number(b.dataset.analyticsUsersCount) || 0
      const diff = usersB - usersA
      if (diff !== 0) return diff

      // Rule 4: Stable tie-break by initial server sequence
      return Number(a.dataset.initialIndex || 0) - Number(b.dataset.initialIndex || 0)
    })

    // Avoid DOM mutations when order does not change
    const hasOrderChanged = sortedFrames.some((frame, index) => frame !== currentFrames[index])
    if (!hasOrderChanged) return

    // Preserve active focus across DOM reordering
    const focusedElement = document.activeElement
    const gridContainsFocus = grid.contains(focusedElement)

    grid.append(...sortedFrames)

    if (gridContainsFocus && focusedElement && document.activeElement !== focusedElement) {
      focusedElement.focus()
    }
  }

  updateSummary() {
    let totalUsers = 0
    let totalViews = 0
    let loadedCount = 0
    const totalFrames = this.frameTargets.length

    this.frameTargets.forEach(frame => {
      const loaded = frame.dataset.analyticsLoaded
      if (loaded === "true") {
        loadedCount++
        totalUsers += parseInt(frame.dataset.analyticsUsersCount || "0", 10)
        totalViews += parseInt(frame.dataset.analyticsViewsCount || "0", 10)
      }
    })

    if (this.hasTotalUsersTarget) {
      this.totalUsersTarget.textContent = totalUsers.toLocaleString()
    }
    if (this.hasTotalViewsTarget) {
      this.totalViewsTarget.textContent = totalViews.toLocaleString()
    }
    if (this.hasQueueStatusTarget) {
      this.queueStatusTarget.textContent = `${loadedCount} / ${totalFrames}`
    }
  }

  buildErrorElement(analyticsKey) {
    const container = document.createElement("div")
    container.className = "bg-white rounded-xl shadow-sm border border-gray-100 p-6 text-center"

    const message = document.createElement("div")
    message.className = "text-sm text-red-600 mb-3"
    message.textContent = "Failed to load analytics."
    container.appendChild(message)

    const button = document.createElement("button")
    button.type = "button"
    button.className = "inline-flex items-center gap-1 px-3 py-1.5 bg-indigo-100 hover:bg-indigo-200 text-indigo-800 text-xs font-bold rounded transition cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 focus-visible:ring-offset-2"
    button.setAttribute("data-action", "analytics-monitor#retryFrame")
    button.setAttribute("data-analytics-key", analyticsKey)
    button.setAttribute("aria-label", "Retry loading analytics")

    const icon = document.createElement("span")
    icon.className = "material-symbols-sharp text-sm"
    icon.setAttribute("aria-hidden", "true")
    icon.textContent = "refresh"
    button.appendChild(icon)

    const label = document.createTextNode("Retry")
    button.appendChild(label)

    container.appendChild(button)
    return container
  }
}
